import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Alert, Descriptions, Spin, Form } from 'antd';
import AutocompleteSelect from './AutocompleteSelect';
import '../assets/scss/RackManagementModal.scss';

const RackManagementModal = ({ 
    visible, 
    onClose, 
    product, 
    branchId,
    rackOptions = [],
    onRackUpdate 
}) => {
    const [rackHistory, setRackHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [rackDetails, setRackDetails] = useState({
        rack1: '',
        rack2: ''
    });
    const [updateMessage, setUpdateMessage] = useState({ type: '', message: '' });
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset states when modal closes
    const handleClose = () => {
        setIsEditing(false);
        setRackDetails({
            rack1: '',
            rack2: ''
        });
        setUpdateMessage({ type: '', message: '' });
        setShowConfirmation(false);
        onClose();
    };

    // Fetch rack details and history when modal is shown
    useEffect(() => {
        if (visible && product?.item_id) {
            fetchRackDetails();
            fetchRackHistory();
        }
    }, [visible, product]);

    const fetchRackDetails = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch(
                `https://live.jeyaagency.com/api/item/rack?branch_id=${branchId}&item_id=${product.item_id}&token=${token}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if (data.status_code === "0") {
                setRackDetails({
                    rack1: data.items?.rack_no || "",
                    rack2: data.items?.rack_no2 || "",
                });
                setIsEditing(false);
            }
        } catch (error) {
            console.error('Error fetching rack details:', error);
            setUpdateMessage({
                type: 'error',
                message: 'Error fetching rack details'
            });
        }
    };

    const fetchRackHistory = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch(
                `https://live.jeyaagency.com/api/item/rack/history?branch_id=${branchId}&item_id=${product.item_id}&token=${token}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if(data.status_code === "0"){
                setRackHistory(data.result || []);
            }
        } catch (error) {
            console.error('Error fetching rack history:', error);
            setUpdateMessage({
                type: 'error',
                message: 'Error fetching rack history'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRackUpdate = async () => {
        if (!rackDetails.rack1) {
            setUpdateMessage({
                type: 'error',
                message: 'Please enter at least Rack 1'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('userToken');
            const userId = localStorage.getItem('userId');
            const response = await fetch('https://live.jeyaagency.com/api/item/rack/update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    item_id: product.item_id,
                    user_id: userId,
                    branch_id: branchId,
                    rack_no: rackDetails.rack1,
                    rack_no2: rackDetails.rack2,
                    token: token
                })
            });

            const data = await response.json();
            if (data.status_code =="0") {
                setUpdateMessage({
                    type: 'success',
                    message: 'Rack details updated successfully!'
                });
                setIsEditing(false);
                // Refresh data
                fetchRackDetails();
                fetchRackHistory();
                // Notify parent component about the update
                if (onRackUpdate) {
                    onRackUpdate();
                }
            } else {
                throw new Error(data.message || 'Failed to update rack details');
            }
        } catch (error) {
            console.error('Error updating rack details:', error);
            setUpdateMessage({
                type: 'error',
                message: error.message || 'Error updating rack details'
            });
        } finally {
            setIsSubmitting(false);
            setShowConfirmation(false);
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString()
        },
        {
            title: 'User',
            dataIndex: 'updated_by_name',
            key: 'updated_by_name'
        },
        {
            title: 'Rack 1',
            dataIndex: 'rack_no',
            key: 'rack_no'
        },
        {
            title: 'Rack 2',
            dataIndex: 'rack_no2',
            key: 'rack_no2'
        }
    ];

    return (
        <Modal
            title={`Rack Management - ${product?.item_name || 'Product'}`}
            open={visible}
            onCancel={handleClose}
            width={800}
            footer={[
                !isEditing ? (
                    <Button key="edit" type="primary" onClick={() => setIsEditing(true)}>
                        Edit Rack Details
                    </Button>
                ) : [
                    <Button key="cancel" onClick={() => {
                        setIsEditing(false);
                        fetchRackDetails(); // Reset to original values
                    }}>
                        Cancel
                    </Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        onClick={() => setShowConfirmation(true)}
                        loading={isSubmitting}
                    >
                        Save Changes
                    </Button>
                ],
                <Button key="close" onClick={handleClose}>
                    Close
                </Button>
            ]}
        >
            {updateMessage.message && (
                <Alert
                    type={updateMessage.type}
                    message={updateMessage.message}
                    showIcon
                    style={{ marginBottom: 16 }}
                    closable
                    onClose={() => setUpdateMessage({ type: '', message: '' })}
                />
            )}

            <div className="rack-details">
                <h3>Current Rack Details</h3>
                <Descriptions bordered column={1}>
                    <Descriptions.Item label="Rack 1">
                        {isEditing ? (
                            <AutocompleteSelect
                                options={rackOptions.map(rack => ({
                                    value: rack.rack_no,
                                    label: rack.rack_no
                                }))}
                                value={rackDetails.rack1}
                                onChange={(value) => setRackDetails({...rackDetails, rack1: value})}
                                placeholder="Select Rack 1"
                                style={{ width: '100%' }}
                                allowClear
                                displayProperty="label"
                                valueProperty="value"
                                required
                            />
                        ) : (
                            rackDetails.rack1 || 'Not set'
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Rack 2">
                        {isEditing ? (
                            <AutocompleteSelect
                                options={rackOptions.map(rack => ({
                                    value: rack.rack_no,
                                    label: rack.rack_no
                                }))}
                                value={rackDetails.rack2}
                                onChange={(value) => setRackDetails({...rackDetails, rack2: value})}
                                placeholder="Select Rack 2 (optional)"
                                style={{ width: '100%' }}
                                allowClear
                                displayProperty="label"
                                valueProperty="value"
                            />
                        ) : (
                            rackDetails.rack2 || 'Not set'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </div>

            <div className="rack-history" style={{ marginTop: 24 }}>
                <h3>Rack Update History</h3>
                <Spin spinning={isLoading}>
                    <Table 
                        columns={columns} 
                        dataSource={rackHistory} 
                        rowKey="id"
                        pagination={{ pageSize: 5 }}
                        size="small"
                    />
                </Spin>
            </div>

            <Modal
                title="Confirm Update"
                open={showConfirmation}
                onOk={handleRackUpdate}
                onCancel={() => setShowConfirmation(false)}
                confirmLoading={isSubmitting}
            >
                <p>Are you sure you want to update the rack details for this product?</p>
                <p><strong>Rack 1:</strong> {rackDetails.rack1 || 'Not set'}</p>
                <p><strong>Rack 2:</strong> {rackDetails.rack2 || 'Not set'}</p>
            </Modal>
        </Modal>
    );
};

export default RackManagementModal;
